import { IconBell, IconCalendar, IconChevronRight, IconLanguage, IconTrash } from '@tabler/icons-react-native';
import { withErrorBoundary } from 'app/components/error-boundary';
import { useOpenSettingsPrompt } from 'app/components/open-settings-prompt';
import { ProfileShortInfo } from 'app/components/profile/profile-short-info';
import Responsive from 'app/components/responsive';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { useTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import { WhiteLabel } from 'app/types/white-labels';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { isWhiteLabel } from 'app/utils/helpers';
import { usePageTitle, useUISize } from 'app/utils/web-helpers';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DeleteAccountButton } from '../account-deletion';
import { LanguageScreenAvailables } from './language';
import { NotificationPreferences } from './notifications/preferences';
import { useNavigationOptions } from 'app/hooks/navigation';

const MyPreferencesScreenComponent: NavigationScreen<'my-preferences'> = ({ navigation }) => {
  const theme = useTheme();
  const { t } = useScopedTranslation();
  const { t: tAccountDeletion } = useTranslation('account-deletion');
  const { openSettingsPrompt } = useOpenSettingsPrompt();

  const setPageTitle = usePageTitle('my-preferences');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  const { isSmallUI, isLargeUI } = useUISize();
  const showLanguagePreference = !isWhiteLabel(WhiteLabel.HauteGaspesie);
  const iconDimensions = isSmallUI ? { height: 20, width: 20 } : { height: 30, width: 30 };
  const iconStyles = { ...iconDimensions, color: theme.colors['text.primary'] };

  const items = [
    ...(isSmallUI
      ? [
          {
            label: t('calendarButtonText'),
            route: 'calendar-setting',
            icon: <IconCalendar strokeWidth={ICON_STROKE_WIDTH} {...iconStyles} />,
          },
        ]
      : []),
    {
      label: t('notificationsButtonText'),
      route: 'notifications-setting',
      icon: <IconBell strokeWidth={ICON_STROKE_WIDTH} {...iconStyles} />,
    },
    ...(showLanguagePreference
      ? [
          {
            label: t('languageButtonText'),
            route: 'language',
            icon: <IconLanguage strokeWidth={ICON_STROKE_WIDTH} {...iconStyles} />,
          },
        ]
      : []),
    {
      label: t('deleteAccountButtonText'),
      route: 'account-deletion',
      icon: <IconTrash strokeWidth={ICON_STROKE_WIDTH} {...iconDimensions} />,
    },
  ] as const;

  return (
    <Page title={t('title')}>
      <Column gap="spacing.general.x4">
        <Responsive
          mobile={items.map(({ label, route, icon }) => (
            <Button
              key={route}
              onPress={() =>
                navigation.navigate(
                  route as 'notifications-setting' | 'calendar-setting' | 'language' | 'account-deletion',
                )
              }
              style={{ padding: 0 }}
              suffix={<IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />}
            >
              <Row flex={1} alignItems="center" gap="spacing.general.x2">
                {icon}
                <Text variant="bodyBase">{label}</Text>
              </Row>
            </Button>
          ))}
          desktop={
            <>
              <NotificationPreferences openSettingsPrompt={openSettingsPrompt} />
              {showLanguagePreference ? <LanguageScreenAvailables /> : null}
              <>
                {isLargeUI ? (
                  <Text
                    fontWeight="700"
                    textAlign="left"
                    variant="bodyLarge"
                    marginTop={showLanguagePreference ? undefined : 'spacing.general.x4'}
                    marginBottom="spacing.general.x2"
                  >
                    {tAccountDeletion('title')}
                  </Text>
                ) : null}

                <Column
                  borderWidth={isLargeUI ? 1 : 0}
                  borderColor="surface.disabled"
                  borderRadius="radius.lg"
                  padding={isLargeUI ? 'spacing.general.x8' : undefined}
                >
                  <Row alignItems="center" gap="spacing.general.x4" minWidth={0}>
                    <Column flex={1} minWidth={0}>
                      <ProfileShortInfo showDetail={false} alignment="flex-start" thumbSize={50} />
                    </Column>
                    <DeleteAccountButton
                      content={
                        <Row justifyContent="center" alignItems="center" gap="spacing.general.x2">
                          <ProfileShortInfo onlyThumb alignment="flex-start" thumbSize={90} />
                        </Row>
                      }
                    />
                  </Row>

                  <Text marginTop="spacing.general.x4">{tAccountDeletion('disclaimer')}</Text>
                </Column>
              </>
            </>
          }
        />
      </Column>
    </Page>
  );
};

export const MyPreferencesScreen: NavigationScreen<'my-preferences'> = withErrorBoundary(
  withDashboardLayout(MyPreferencesScreenComponent),
);
